import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useMarkNotificationReadForEntity } from "../../context/NotificationContext";
import { Header } from "../../components/vc/Header";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section, EmptyState } from "../../components/vc/layout";
import { ImageViewer } from "../../components/vc/ImageViewer";
import { ImageStrip } from "../../components/vc/ImageStrip";
import { computeRetention } from "../../lib/retention";
import {
  ArrowLeft, Package, Building2, Clock, CheckCircle2, ShoppingBag, Calendar, MessageCircle, Sparkles, Trash2, ChevronDown, Plus,
} from "lucide-react";
import { toast } from "sonner";

const displayStatus = (raw, items) => {
  if (raw === "quoted" || (items || []).some((it) => (it.quotes || []).length > 0)) return "responded";
  if (raw === "closed" || ((items || []).length > 0 && items.every((it) => it.order_id))) return "closed";
  return "pending";
};

export default function VCEnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enq, setEnq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/bulk-price-requests/${id}`)
      .then((r) => setEnq(r.data))
      .catch(() => { toast.error("Enquiry not found"); navigate("/enquiries"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  // #3 — viewing this enquiry marks its "responded" notification read.
  useMarkNotificationReadForEntity(id);

  const status = useMemo(() => enq ? displayStatus(enq.status, enq.items) : "pending", [enq]);
  const quotedItems = useMemo(() => (enq?.items || []).filter((it) => (it.quotes || []).length > 0), [enq]);
  const orderedItems = useMemo(() => (enq?.items || []).filter((it) => it.order_id), [enq]);
  const readyToOrder = useMemo(
    () => quotedItems.filter((it) => !it.order_id),
    [quotedItems]
  );

  // #7 — Item selection when converting quotations to orders.
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  // Per-item chosen supplier. Defaults to the cheapest (best_supplier_id) as a
  // recommendation but the user can override to any supplier that has quoted.
  const [chosenSupplier, setChosenSupplier] = useState({});
  useEffect(() => {
    // When quotes load, default to ALL ready items selected.
    setSelectedIdx(new Set(readyToOrder.map((it) => it.index)));
    // Seed chosen suppliers from best_supplier_id where available.
    setChosenSupplier((prev) => {
      const next = { ...prev };
      for (const it of readyToOrder) {
        if (next[it.index]) continue;
        const preferred = it.best_supplier_id
          || (it.quotes && it.quotes.length > 0 ? it.quotes[0].supplier_id : null);
        if (preferred) next[it.index] = preferred;
      }
      return next;
    });
  }, [readyToOrder.length]);
  const chooseSupplier = (idx, sid) =>
    setChosenSupplier((prev) => ({ ...prev, [idx]: sid }));
  // Item-wise price negotiation — buyer may optionally propose a different
  // price than the chosen quote. Supplier sees both and picks one later.
  const [negotiatedPrices, setNegotiatedPrices] = useState({});
  const setNegotiatedPrice = (idx, val) => setNegotiatedPrices((p) => ({ ...p, [idx]: val }));
  const toggleItem = (idx) => setSelectedIdx((s) => {
    const n = new Set(s); if (n.has(idx)) n.delete(idx); else n.add(idx); return n;
  });
  const toggleAll = () => setSelectedIdx((s) => (
    s.size === readyToOrder.length ? new Set() : new Set(readyToOrder.map((it) => it.index))
  ));
  const selectedReady = readyToOrder.filter((it) => selectedIdx.has(it.index));

  // Group the currently-selected items by the chosen supplier so the user can
  // see exactly how many separate orders will be created before placing.
  const groupsBySupplier = useMemo(() => {
    const map = new Map();
    for (const it of selectedReady) {
      const sid = chosenSupplier[it.index] || it.best_supplier_id;
      if (!sid) continue;
      const arr = map.get(sid) || [];
      arr.push(it);
      map.set(sid, arr);
    }
    return map;
  }, [selectedReady, chosenSupplier]);

  const placeOrders = async () => {
    if (selectedReady.length === 0) return;
    setPlacing(true);
    try {
      const selections = selectedReady.map((it) => {
        const raw = negotiatedPrices[it.index];
        const neg = raw !== undefined && raw !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : undefined;
        return {
          index: it.index,
          supplier_id: chosenSupplier[it.index] || it.best_supplier_id || it.supplier_id,
          ...(neg !== undefined ? { negotiated_price: neg } : {}),
        };
      });
      await apiClient.post("/bulk-orders", {
        bulk_request_id: enq.id,
        selections,
      });
      const groupCount = groupsBySupplier.size || 1;
      toast.success(`Placed ${groupCount} order${groupCount === 1 ? "" : "s"} across ${groupCount} supplier${groupCount === 1 ? "" : "s"}`);
      load();
      setTimeout(() => navigate("/orders"), 800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to place orders");
    } finally {
      setPlacing(false);
    }
  };

  const deleteEnquiry = async () => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      await apiClient.delete(`/bulk-price-requests/${id}`);
      toast.success("Enquiry deleted");
      navigate("/enquiries");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  if (loading || !enq) {
    return (
      <>
        <Header />
        <div className="px-4 py-8 text-center text-slate-500">Loading enquiry...</div>
      </>
    );
  }

  const dateStr = new Date(enq.created_at).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-4">
        <button
          onClick={() => navigate("/enquiries")}
          data-testid="enq-detail-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header card */}
        <div data-testid="enq-detail-card" className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
                <Sparkles className="w-3 h-3" /> Enquiry
              </div>
              {enq.title ? (
                <>
                  <div data-testid="enq-detail-title" className="text-lg font-bold text-slate-900 leading-tight">
                    {enq.title}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                    #{(enq.id || "").slice(0, 8).toUpperCase()}
                  </div>
                </>
              ) : (
                <div className="text-lg font-bold text-slate-900 leading-tight">
                  #{(enq.id || "").slice(0, 8).toUpperCase()}
                </div>
              )}
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> {dateStr}
              </div>
              {(() => {
                const retention = computeRetention({
                  status: enq.status,
                  created_at: enq.created_at,
                  last_response_at: enq.last_response_at,
                });
                if (!retention || status === "closed") return null;
                const tone = retention.daysLeft <= 7 ? "text-amber-700 bg-amber-50" : "text-slate-600 bg-slate-100";
                return (
                  <div
                    data-testid="enq-detail-retention"
                    className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold ${tone}`}
                  >
                    <Clock className="w-3 h-3" strokeWidth={2.4} />
                    Auto deletion in: {retention.daysLeft} {retention.daysLeft === 1 ? "Day" : "Days"}
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={status} size="lg" testid="enq-detail-status" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
            <MiniStat label="Items" value={enq.items?.length || 0} Icon={Package} tone="indigo" />
            <MiniStat label="Quoted" value={quotedItems.length} Icon={CheckCircle2} tone="emerald" />
            <MiniStat label="Ordered" value={orderedItems.length} Icon={ShoppingBag} tone="sky" />
          </div>
        </div>

        {/* Attached enquiry image(s). Newer enquiries use `images` (up to 5); legacy use `image_data`. */}
        {enq.images && enq.images.length > 0 ? (
          <div data-testid="enq-images-block" className="bg-white rounded-2xl border border-slate-100 p-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1 pb-2">
              {enq.images.length} attached {enq.images.length === 1 ? "image" : "images"}
            </div>
            <ImageStrip images={enq.images} testid="enq-image-strip" />
          </div>
        ) : (enq.image_data && (
          <ImageViewer src={enq.image_data} label="Enquiry attachment" testid="enq-image-viewer" />
        ))}

        {/* Businesses section — each is tappable to reveal that business's full quotation */}
        <BusinessSection enq={enq} />

        {/* Items — each with assigned business + quote (if any) */}
        <Section title="Items in this enquiry" subtitle={`${enq.items?.length || 0} rows`} testid="enq-items-section">
          {status !== "closed" && (
            <div className="flex items-center justify-between gap-2 pb-2" data-testid="enq-select-all-bar">
              {readyToOrder.length > 0 ? (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIdx.size === readyToOrder.length && readyToOrder.length > 0}
                    onChange={toggleAll}
                    data-testid="enq-select-all"
                    className="w-4 h-4 rounded accent-emerald-600"
                  />
                  <span className="text-xs font-semibold text-slate-600">
                    Select All ({selectedIdx.size}/{readyToOrder.length} selected)
                  </span>
                </div>
              ) : <span />}
              {/* #2 — labeled "Add an item" button, placed beside Select All */}
              <button
                onClick={() => navigate(`/enquiries/${id}/add-items`)}
                data-testid="enq-detail-add-items"
                className="flex items-center gap-1.5 pl-2.5 pr-3 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm shadow-emerald-500/30 transition shrink-0"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2.6} /> Add an item
              </button>
            </div>
          )}
          <div className="space-y-2" data-testid="enq-items-list">
            {(enq.items || []).map((it) => (
              <div key={it.index} className="flex items-start gap-2">
                {readyToOrder.some((r) => r.index === it.index) && (
                  <input
                    type="checkbox"
                    checked={selectedIdx.has(it.index)}
                    onChange={() => toggleItem(it.index)}
                    data-testid={`enq-item-select-${it.index}`}
                    className="w-4 h-4 mt-3 rounded accent-emerald-600 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <ItemRow
                    item={it}
                    chosenSupplierId={chosenSupplier[it.index]}
                    onChooseSupplier={(sid) => chooseSupplier(it.index, sid)}
                    negotiatedPrice={negotiatedPrices[it.index]}
                    onNegotiatedPriceChange={(v) => setNegotiatedPrice(it.index, v)}
                  />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Action */}
        {selectedReady.length > 0 && (
          <>
            {groupsBySupplier.size > 1 && (
              <div
                data-testid="enq-order-split-summary"
                className="bg-indigo-50 border border-indigo-100 rounded-2xl px-3 py-2.5 text-[11px] font-semibold text-indigo-800"
              >
                Will create {groupsBySupplier.size} separate orders — one per selected supplier ({Array.from(groupsBySupplier.entries()).map(([sid, items]) => {
                  const name = items[0].quotes?.find((q) => q.supplier_id === sid)?.supplier_name || (enq.supplier_names || {})[sid] || "Business";
                  return `${name} × ${items.length}`;
                }).join(" · ")})
              </div>
            )}
            <PrimaryButton
              onClick={placeOrders}
              loading={placing}
              data-testid="enq-place-orders-btn"
              className="w-full h-14 flex items-center justify-center gap-2 text-base"
            >
              <ShoppingBag className="w-5 h-5" />
              Convert {selectedReady.length} quote{selectedReady.length === 1 ? "" : "s"} to {groupsBySupplier.size > 1 ? `${groupsBySupplier.size} orders` : "order"}
            </PrimaryButton>
          </>
        )}
        {quotedItems.length === 0 && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-amber-800 flex items-start gap-3">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Waiting for quotations</div>
              <div className="text-xs mt-0.5 text-amber-700">Businesses assigned above will reply from their app. Quotes will appear here.</div>
            </div>
          </div>
        )}

        {/* #8 — Delete this enquiry from my records. Does NOT cascade to placed orders. */}
        <button
          type="button"
          onClick={deleteEnquiry}
          data-testid="enq-delete-btn"
          className="w-full text-xs font-semibold text-red-600 hover:text-red-700 flex items-center justify-center gap-1 pt-2"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete this enquiry
        </button>
      </div>
    </>
  );
}

function MiniStat({ label, value, Icon, tone = "slate" }) {
  const tones = {
    indigo: "text-indigo-700 bg-indigo-50",
    emerald: "text-emerald-700 bg-emerald-50",
    sky: "text-sky-700 bg-sky-50",
    slate: "text-slate-700 bg-slate-50",
  };
  const cls = tones[tone] || tones.slate;
  return (
    <div className={`rounded-2xl p-2.5 text-center ${cls.split(" ")[1]}`}>
      <Icon className={`w-4 h-4 mx-auto ${cls.split(" ")[0]}`} strokeWidth={2.4} />
      <div className={`text-xl font-bold leading-none mt-1 ${cls.split(" ")[0]}`}>{value}</div>
      <div className={`text-[9px] uppercase tracking-wider font-bold mt-1 ${cls.split(" ")[0]}`}>{label}</div>
    </div>
  );
}

function ItemRow({ item, chosenSupplierId, onChooseSupplier, negotiatedPrice, onNegotiatedPriceChange }) {
  const quotes = item.quotes || [];
  const ordered = !!item.order_id;
  const anyQuote = quotes.length > 0;
  const state = ordered ? "ordered" : anyQuote ? "responded" : "pending";

  // Sort quotes by lowest total price for price comparison.
  const sortedQuotes = [...quotes].sort(
    (a, b) => Number(a.price || 0) - Number(b.price || 0)
  );
  const bestPrice = sortedQuotes[0]?.price;
  const bestSupplierId = sortedQuotes[0]?.supplier_id || item.best_supplier_id;
  // The supplier the user has picked for this item (default recommendation = cheapest).
  const activeSupplierId = chosenSupplierId || bestSupplierId;
  const canChoose = !ordered && quotes.length > 0;

  return (
    <div
      data-testid={`enq-item-${item.index}`}
      className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900">{item.product}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {item.quantity} {item.unit}
          </div>
          <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
            <Building2 className="w-3 h-3" />
            <span data-testid={`enq-item-suppliers-${item.index}`}>
              {(item.supplier_ids && item.supplier_ids.length > 0
                ? item.supplier_ids.map((sid) => item.supplier_names?.[sid] || sid).join(", ")
                : item.supplier_name) || "—"}
            </span>
          </div>
        </div>
        <StatusBadge
          status={state === "ordered" ? "delivered" : state === "responded" ? "responded" : "pending"}
          label={state === "ordered" ? "Ordered" : state === "responded" ? "Responded" : "Pending"}
        />
      </div>

      {/* Price Comparison — every quote is a tappable radio-style card. Cheapest = "Best" recommendation. */}
      {anyQuote && (
        <div data-testid={`enq-item-quotes-${item.index}`} className="mt-3 pt-3 border-t border-slate-100 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 flex items-center justify-between gap-2">
            <span>{quotes.length > 1 ? `Choose supplier · ${quotes.length} quotes` : "Quote received"}</span>
            {canChoose && quotes.length > 1 && (
              <span className="text-slate-400 font-normal normal-case text-[10px]">Tap to change</span>
            )}
          </div>
          <div className="space-y-1.5">
            {sortedQuotes.map((q, qi) => {
              const isBest = quotes.length > 1 && Number(q.price || 0) === Number(bestPrice || 0);
              const isActive = q.supplier_id === activeSupplierId;
              const disc = Number(q.discount || 0);
              const unit = Number(q.unit_price || 0);
              const tone = ordered
                ? isActive
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-slate-50 border-slate-100"
                : isActive
                  ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/30"
                  : isBest
                    ? "bg-slate-50 border-emerald-200"
                    : "bg-slate-50 border-slate-100 hover:border-slate-300";
              return (
                <button
                  key={q.supplier_id + "-" + qi}
                  type="button"
                  onClick={canChoose ? () => onChooseSupplier?.(q.supplier_id) : undefined}
                  disabled={!canChoose}
                  data-testid={`enq-quote-${item.index}-${q.supplier_id}`}
                  data-selected={isActive ? "true" : "false"}
                  aria-pressed={isActive}
                  className={`w-full text-left rounded-xl p-2.5 border transition ${tone} ${canChoose ? "cursor-pointer active:scale-[0.99]" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    {canChoose && (
                      <div
                        className={`w-4 h-4 rounded-full border-2 shrink-0 grid place-items-center ${
                          isActive ? "border-emerald-600 bg-emerald-600" : "border-slate-300 bg-white"
                        }`}
                        aria-hidden="true"
                      >
                        {isActive && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="text-xs font-bold text-slate-900 truncate">{q.supplier_name}</div>
                        {isBest && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wider"
                            title="Lowest price recommendation"
                          >
                            Best
                          </span>
                        )}
                        {isActive && !isBest && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white uppercase tracking-wider">
                            Chosen
                          </span>
                        )}
                      </div>
                      {(unit > 0 || disc > 0) && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {unit > 0 ? `Unit ₹${unit.toFixed(2)}` : ""}
                          {disc > 0 ? ` · Discount ${disc}%` : ""}
                        </div>
                      )}
                      {q.note && (
                        <div className="text-[11px] text-slate-500 mt-0.5 italic truncate">&ldquo;{q.note}&rdquo;</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-base font-bold ${isActive ? "text-emerald-700" : "text-slate-800"}`}>
                        ₹{Number(q.price || 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wider">Total</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {canChoose && (
            <div className="pt-1">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Propose a price (optional)
              </label>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-sm font-bold text-slate-400">₹</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder={`e.g. a lower total than ₹${Number(bestPrice || 0).toFixed(2)}`}
                  value={negotiatedPrice ?? ""}
                  onChange={(e) => onNegotiatedPriceChange?.(e.target.value)}
                  data-testid={`enq-negotiated-price-${item.index}`}
                  className="flex-1 min-w-0 h-9 px-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                The business will see both prices and pick one before confirming your order.
              </div>
            </div>
          )}
        </div>
      )}
      {!anyQuote && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Waiting for {item.supplier_name || "business"} to respond
        </div>
      )}
    </div>
  );
}


// -------- Business Section (per-business status + tap-to-expand quotation) --------
function BusinessSection({ enq }) {
  const [openId, setOpenId] = useState(null);
  const supplierIds = enq.supplier_ids || Object.keys(enq.supplier_names || {});
  if (supplierIds.length === 0) return null;

  const perBiz = supplierIds.map((sid) => {
    const name = (enq.supplier_names || {})[sid] || "Business";
    const itemsForBiz = (enq.items || []).filter((it) =>
      (it.supplier_ids || [it.supplier_id]).includes(sid)
    );
    const quoteRows = itemsForBiz.map((it) => {
      const qt = (it.quotes || []).find((q) => q.supplier_id === sid) || null;
      return { item: it, quote: qt };
    });
    const quoted = quoteRows.filter((r) => !!r.quote);
    let bizStatus = "pending";
    if (itemsForBiz.length > 0 && quoted.length === itemsForBiz.length) bizStatus = "responded";
    else if (quoted.length > 0) bizStatus = "partial";
    const respTimes = quoted
      .map((r) => r.quote?.responded_at)
      .filter(Boolean)
      .sort();
    const respondedAt = respTimes.length ? respTimes[respTimes.length - 1] : null;
    return { sid, name, bizStatus, quoteRows, respondedAt };
  });

  const respondedCount = perBiz.filter((p) => p.bizStatus === "responded" || p.bizStatus === "partial").length;

  return (
    <Section
      title="Businesses"
      subtitle={`${respondedCount} of ${perBiz.length} Responded`}
      testid="enq-detail-business-section"
    >
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100 overflow-hidden">
        {perBiz.map(({ sid, name, bizStatus, quoteRows, respondedAt }) => {
          const dot =
            bizStatus === "responded"
              ? "bg-emerald-500"
              : bizStatus === "partial"
                ? "bg-amber-400"
                : "bg-slate-300";
          const labelText =
            bizStatus === "responded"
              ? "Responded"
              : bizStatus === "partial"
                ? "Partially Responded"
                : "Response Pending";
          const labelTone =
            bizStatus === "responded"
              ? "text-emerald-700"
              : bizStatus === "partial"
                ? "text-amber-700"
                : "text-slate-500";
          const isOpen = openId === sid;
          const respStr = respondedAt
            ? new Date(respondedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
            : null;
          return (
            <div key={sid} data-testid={`enq-detail-biz-${sid}`}>
              <button
                onClick={() => setOpenId((cur) => (cur === sid ? null : sid))}
                data-testid={`enq-detail-biz-toggle-${sid}`}
                className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-slate-50 transition text-left"
              >
                <span className={`w-2 h-2 rounded-full ${dot} shrink-0`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{name}</div>
                  <div className={`text-[11px] font-semibold ${labelTone} flex items-center gap-1.5 flex-wrap`}>
                    <span>{labelText}</span>
                    {respStr && <span className="text-slate-400 font-normal">· {respStr}</span>}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-3 space-y-2" data-testid={`enq-detail-biz-body-${sid}`}>
                  {quoteRows.length === 0 ? (
                    <div className="text-xs text-slate-500">No items assigned to this business.</div>
                  ) : (
                    quoteRows.map((row, idx) => (
                      <BizQuoteLine key={idx} idx={idx} row={row} testid={`enq-detail-biz-quote-${sid}-${idx}`} />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function BizQuoteLine({ row, idx, testid }) {
  const { item, quote } = row;
  const hasQuote = !!quote;
  const qty = Number(item.quantity || 0);
  const unitPrice = hasQuote && quote.unit_price != null ? Number(quote.unit_price) : null;
  const discount = hasQuote ? Number(quote.discount || 0) : 0;
  const total = hasQuote ? Number(quote.price || 0) : 0;
  const respStr = hasQuote && quote.responded_at
    ? new Date(quote.responded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-2.5 ${hasQuote ? "border-emerald-100 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-slate-400 tabular-nums w-5 shrink-0">{idx + 1}.</span>
        <div className="min-w-0 flex-1 font-semibold text-slate-900 truncate">
          {item.product || "(image-only item)"}
        </div>
        <div className="text-[11px] text-slate-500 shrink-0 tabular-nums">
          {qty} {item.unit}
        </div>
      </div>
      {hasQuote ? (
        <div className="mt-1 pl-6 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
          {unitPrice != null && <span className="tabular-nums">Unit ₹{unitPrice.toFixed(2)}</span>}
          {discount > 0 && <span className="text-amber-700">−{discount}% discount</span>}
          <span className="tabular-nums font-semibold text-slate-900">Total ₹{total.toFixed(2)}</span>
          {quote.note && <span className="italic text-slate-500 truncate">&ldquo;{quote.note}&rdquo;</span>}
          {respStr && <span className="text-slate-400">Received {respStr}</span>}
        </div>
      ) : (
        <div className="mt-1 pl-6 text-[11px] text-slate-500 italic">Awaiting quotation</div>
      )}
    </div>
  );
}
